import { useState } from 'react'
import { Search, Plus, Package, Pencil, Trash2, PackageCheck, PackageX, Tag } from 'lucide-react'
import { Modal, ConfirmModal } from '../common/Modal'

const ITEMS = [
  { id: 'ITM-001', name: 'Enterprise Suite', category: 'Software', price: '$2,999', stock: 'Unlimited', status: 'active' },
  { id: 'ITM-002', name: 'Pro License', category: 'Software', price: '$799', stock: 'Unlimited', status: 'active' },
  { id: 'ITM-003', name: 'API Add-on Pack', category: 'Add-on', price: '$149', stock: '500', status: 'active' },
  { id: 'ITM-004', name: 'Data Export Module', category: 'Add-on', price: '$99', stock: '200', status: 'active' },
  { id: 'ITM-005', name: 'Priority Support', category: 'Service', price: '$499/yr', stock: 'Unlimited', status: 'active' },
  { id: 'ITM-006', name: 'Custom Branding', category: 'Service', price: '$299', stock: '150', status: 'inactive' },
  { id: 'ITM-007', name: 'SSO Integration', category: 'Feature', price: '$199', stock: 'Unlimited', status: 'active' },
]

export default function ItemManagement() {
  const [items, setItems] = useState(ITEMS)
  const [search, setSearch] = useState('')
  const [modalOpen, setModalOpen] = useState(false)
  const [deleteOpen, setDeleteOpen] = useState(false)
  const [active, setActive] = useState(null)
  const [form, setForm] = useState({ name: '', category: 'Software', price: '$99', stock: '100', status: 'active' })

  const filtered = items.filter(i =>
    i.name.toLowerCase().includes(search.toLowerCase()) ||
    i.category.toLowerCase().includes(search.toLowerCase())
  )

  const openAdd = () => {
    setActive(null)
    setForm({ name: '', category: 'Software', price: '$99', stock: '100', status: 'active' })
    setModalOpen(true)
  }

  const openEdit = (item) => {
    setActive(item)
    setForm({ name: item.name, category: item.category, price: item.price, stock: item.stock, status: item.status })
    setModalOpen(true)
  }

  const save = () => {
    if (!form.name.trim()) return
    if (active) {
      setItems(prev => prev.map(i => i.id === active.id ? { ...i, ...form } : i))
    } else {
      const id = `ITM-${String(items.length + 1).padStart(3, '0')}`
      setItems(prev => [{ id, ...form }, ...prev])
    }
    setModalOpen(false)
  }

  const onDelete = () => {
    setItems(prev => prev.filter(i => i.id !== active.id))
    setDeleteOpen(false)
    setActive(null)
  }

  const activeCount = items.filter(i => i.status === 'active').length
  const inactiveCount = items.filter(i => i.status === 'inactive').length
  const catCount = new Set(items.map(i => i.category)).size

  const stats = [
    { label: 'Total Items', value: items.length, icon: Package, color: '#4F46E5', colorGlow: 'rgba(79,70,229,0.18)', trend: '+22%', trendColor: '#4F46E5', bar: 100, variant: 'indigo' },

    { label: 'Active', value: activeCount, icon: PackageCheck, color: '#10B981', colorGlow: 'rgba(16,185,129,0.15)', trend: '+2%', trendColor: '#10B981', bar: Math.round(activeCount / items.length * 100), variant: 'green' },

    { label: 'Inactive', value: inactiveCount, icon: PackageX, color: '#EF4444', colorGlow: 'rgba(239,68,68,0.15)', trend: '+1%', trendColor: '#EF4444', bar: Math.round(inactiveCount / items.length * 100), variant: 'red' },

    { label: 'Categories', value: catCount, icon: Tag, color: '#F59E0B', colorGlow: 'rgba(245,158,11,0.15)', trend: '+4%', trendColor: '#F59E0B', bar: 80, variant: 'amber' },
  ]

  return (
    <div className="dash-page page-in">
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 'var(--space-md)' }}>
        <h1 className="page-title">Item Management</h1>
        <button className="btn btn-primary" onClick={openAdd}><Plus size={14} /> Add Item</button>
      </div>

      <div className="stats-grid cols-4">
        {stats.map(s => (
          <div key={s.label} className={`stat-card stat-card--${s.variant}`}>
            <span style={{ "position": "absolute", "top": "0px", "right": "0px", "width": "110px", "height": "110px", "border-radius": "50%", "background": s.colorGlow, "filter": "blur(30px)", "transform": "translate(30%, -30%)", "pointer-events": "none" }}></span>
            <div className="stat-card-header">
              <div className="stat-card-icon" style={{ background: s.colorGlow, color: s.color, border: `1px solid ${s.colorGlow}` }}>
                <s.icon size={15} strokeWidth={1.8} />
              </div>
              <span className="stat-card-trend" style={{
                color: s.color,
                background: `${s.trendColor}15`,
                border: `1px solid ${s.colorGlow}`
              }}>
                {s.trend}
              </span>
            </div>
            <div className="stat-card-value">{s.value}</div>
            <div className="stat-card-label">{s.label}</div>
            <div style={{ "position": "absolute", "bottom": "0px", "left": "0px", "right": "0px", "height": "2px", background: `linear-gradient(90deg,${s.color}88,transparent)`, "border-radius": "0 0 var(--radius-lg) var(--radius-lg)" }}></div>
          </div>
        ))}
      </div>

      <div className="card" style={{ padding: 0 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-sm)', padding: 'var(--space-md) var(--space-lg)', borderBottom: '1px solid var(--border)', flexWrap: 'wrap' }}>
          <div style={{ position: 'relative', flex: 1, minWidth: 180 }}>
            <Search size={13} style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', color: 'var(--text3)' }} />
            <input className="input" style={{ paddingLeft: 32 }} placeholder="Search items…" value={search} onChange={e => setSearch(e.target.value)} />
          </div>
          <button className="btn btn-outline btn-sm">Filter</button>
        </div>

        <div className="table-wrapper">
          <table className="data-table">
            <thead>
              <tr><th>Item ID</th><th>Item Name</th><th>Category</th><th>Price</th><th>Stock</th><th>Status</th><th></th></tr>
            </thead>
            <tbody>
              {filtered.map(item => (
                <tr key={item.id}>
                  <td style={{ fontFamily: 'var(--font-mono)', fontSize: 'var(--fs-xs)', color: 'var(--accent-hover)' }}>{item.id}</td>
                  <td style={{ fontWeight: 600, color: 'var(--text1)' }}>{item.name}</td>
                  <td><span className="badge badge-gold">{item.category}</span></td>
                  <td style={{ fontFamily: 'var(--font-mono)', fontSize: 'var(--fs-sm)', color: 'var(--color-green)' }}>{item.price}</td>
                  <td>{item.stock}</td>
                  <td><span className={`badge ${item.status === 'active' ? 'badge-active' : 'badge-inactive'}`}>{item.status}</span></td>
                  <td>
                    <div style={{ display: 'flex', gap: 6 }}>
                      <button className="btn btn-ghost btn-icon" onClick={() => openEdit(item)}><Pencil size={14} /></button>
                      <button className="btn btn-ghost btn-icon" onClick={() => { setActive(item); setDeleteOpen(true) }}><Trash2 size={14} /></button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <Modal
        open={modalOpen}
        title={active ? 'Edit Item' : 'Add Item'}
        subtitle="Maintain product and feature catalog"
        onClose={() => setModalOpen(false)}
        footer={(
          <>
            <button className="btn btn-outline" onClick={() => setModalOpen(false)}>Cancel</button>
            <button className="btn btn-primary" onClick={save}>{active ? 'Update Item' : 'Create Item'}</button>
          </>
        )}
      >
        <div className="form-grid cols-2">
          <div className="form-field">
            <label>Item Name</label>
            <input className="input" placeholder="Enter item name" value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} />
          </div>
          <div className="form-field">
            <label>Category</label>
            <select className="input" value={form.category} onChange={e => setForm({ ...form, category: e.target.value })}>
              <option>Software</option><option>Add-on</option><option>Service</option><option>Feature</option>
            </select>
          </div>
          <div className="form-field">
            <label>Price</label>
            <input className="input" placeholder="$199" value={form.price} onChange={e => setForm({ ...form, price: e.target.value })} />
          </div>
          <div className="form-field">
            <label>Stock</label>
            <input className="input" placeholder="e.g. 150 / Unlimited" value={form.stock} onChange={e => setForm({ ...form, stock: e.target.value })} />
          </div>
          <div className="form-field full">
            <label>Status</label>
            <select className="input" value={form.status} onChange={e => setForm({ ...form, status: e.target.value })}>
              <option value="active">active</option>
              <option value="inactive">inactive</option>
            </select>
          </div>
        </div>
      </Modal>

      <ConfirmModal
        open={deleteOpen}
        title="Delete item"
        message={`Delete ${active?.name || 'selected item'} from catalog?`}
        onClose={() => setDeleteOpen(false)}
        onConfirm={onDelete}
      />

      <style>{`.dash-page{display:flex;flex-direction:column;gap:var(--space-lg);width:100%;}`}</style>
    </div>
  )
}